import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const PaymentVerifications = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPendingPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_BASE}/payment/manual-upi/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setPayments(data.pendingPayments);
      } else {
        setError(data.message || 'Failed to fetch pending payments');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const handleAction = async (paymentId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this payment?`)) return;
    
    setActionLoading(paymentId);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_BASE}/payment/manual-upi/${action}/${paymentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Remove from list
        setPayments(payments.filter(p => p._id !== paymentId));
        alert(`Payment ${action}d successfully`);
      } else {
        alert(data.message || `Failed to ${action} payment`);
      }
    } catch (err) {
      alert('Network error occurred');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="text-center py-8"><FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto" /></div>;
  if (error) return <div className="text-red-500 p-4 border border-red-200 bg-red-50 rounded-lg">{error}</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-900">Pending UPI Payments</h2>
        <p className="text-gray-500 text-sm mt-1">Verify UTR numbers against your bank statement before approving.</p>
      </div>

      <div className="p-6">
        {payments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
             No pending UPI payments to review.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="pb-3 text-sm font-semibold text-gray-600">User</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">UTR / Ref No</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Amount (INR)</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Date Submitted</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((payment) => (
                  <tr key={payment._id} className="hover:bg-gray-50">
                    <td className="py-4">
                      <div className="font-medium text-gray-900">{payment.user?.name || 'Unknown'}</div>
                      <div className="text-sm text-gray-500">{payment.user?.email}</div>
                    </td>
                    <td className="py-4">
                      <span className="font-mono bg-gray-100 px-2 py-1 rounded text-gray-800 font-semibold tracking-wider">
                        {payment.stripeSessionId.replace('utr_', '')}
                      </span>
                    </td>
                    <td className="py-4 font-medium text-gray-900">
                      ₹{payment.amount / 100}
                    </td>
                    <td className="py-4 text-sm text-gray-600">
                       {new Date(payment.createdAt).toLocaleString()}
                    </td>
                    <td className="py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleAction(payment._id, 'approve')}
                          disabled={actionLoading === payment._id}
                          className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-200 disabled:opacity-50"
                        >
                          <FaCheckCircle />
                          {actionLoading === payment._id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleAction(payment._id, 'reject')}
                          disabled={actionLoading === payment._id}
                          className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50"
                        >
                          <FaTimesCircle />
                          {actionLoading === payment._id ? '...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentVerifications;
